import { callMethodOnKeydown, isTyping } from '../helpers'
import { state } from '../map_state/state'
import { Renderer } from '../renderer'
import { Sensor } from '../models/sensor'
import { addSensorTool } from './add_sensor_tool'
import { pathcalculatorTool } from './path_calculator_tool'

class HistoryTool {
  constructor () {
    this.undoElement = document.getElementById('mct-undo')
    this.redoElement = document.getElementById('mct-redo')
    this.toggleVisibility()
    this.bindEvents()
  }

  bindEvents () {
    this.undo = this.undo.bind(this)
    this.redo = this.redo.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.undoElement.addEventListener('click', this.undo)
    this.redoElement.addEventListener('click', this.redo)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.undoElement.removeEventListener('click', this.undo)
    this.redoElement.removeEventListener('click', this.redo)
    document.removeEventListener('keydown', this.onKeyDown)
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key === 'z' && !this.undoElement.classList.contains('hidden')) callMethodOnKeydown(e.target, this.undo)
    if (e.key === 'x' && !this.redoElement.classList.contains('hidden')) callMethodOnKeydown(e.target, this.redo)
  }

  undo () {
    this.apply(state.historyIndex - 1)
    Renderer.render()
  }

  redo () {
    this.apply(state.historyIndex + 1)
    Renderer.render()
  }

  toggleVisibility () {
    this.undoElement.classList.toggle('hidden', state.history.length === 0 || state.historyIndex <= 0)
    this.redoElement.classList.toggle('hidden', state.history.length === 0 || state.historyIndex >= state.history.length - 1)
  }

  add () {
    state.history = state.history.slice(0, state.historyIndex + 1)

    state.history.push({
      mapImage: state.mapImage,
      origMap: state.origMap,
      mapWorld: { ...state.mapWorld },
      canEditMap: state.canEditMap,
      cropRect: { ...state.cropRect },
      drag: state.drag ? { ...state.drag } : null,
      selectedSensorId: state.selectedSensorId,
      tracerPointsWorld: state.tracerPointsWorld.map(point => ({ ...point })),
      tracerPathWorld: state.tracerPathWorld.map(point => ({ ...point })),
      lenPath: state.lenPath,
      lenUnit: state.lenUnit,
      calibration: state.calibration,
      cursorWorld: state.cursorWorld ? { ...state.cursorWorld } : null,
      boundaryInfo: state.boundaryInfo ? { ...state.boundaryInfo } : null,
      segments: state.segments.map(segment => ({
        a: segment.a ? { ...segment.a } : null,
        b: segment.b ? { ...segment.b } : null
      })),
      startPoint: state.startPoint ? { ...state.startPoint } : null,
      sensors: state.sensors.map(sensor => sensor.toHistory()),
      sensorNameChangedOnce: state.sensorNameChangedOnce,
      nextSensorId: state.nextSensorId,
      flowStartSensorId: state.flowStartSensorId,
      flowPreviewWorld: state.flowPreviewWorld ? { ...state.flowPreviewWorld } : null,
      groupSensorIds: [...state.groupSensorIds],
      groups: state.groups.map(group => ({
        ...group,
        sensorIds: group.sensorIds ? [...group.sensorIds] : []
      })),
      visibleSensors: state.visibleSensors,
      visiblePointDistances: state.visiblePointDistances,
      visibleLinearFlows: state.visibleLinearFlows,
      visibleLables: state.visibleLables
    })

    state.historyIndex = state.history.length - 1
  }

  apply (index) {
    if (index < 0 || index >= state.history.length) return

    const historyState = state.history[index]

    state.mapImage = historyState.mapImage
    state.origMap = historyState.origMap ?? historyState.mapImage ?? null
    state.mapWorld = { ...historyState.mapWorld }
    state.canEditMap = historyState.canEditMap
    state.cropRect = { ...historyState.cropRect }
    state.drag = historyState.drag ? { ...historyState.drag } : null
    state.tracerPointsWorld = historyState.tracerPointsWorld.map(point => ({ ...point }))
    state.tracerPathWorld = historyState.tracerPathWorld.map(point => ({ ...point }))
    state.lenPath = historyState.lenPath
    state.lenUnit = historyState.lenUnit
    state.calibration = !!historyState.calibration
    state.cursorWorld = historyState.cursorWorld ? { ...historyState.cursorWorld } : null
    state.boundaryInfo = historyState.boundaryInfo ? { ...historyState.boundaryInfo } : null
    state.selectedSensorId = historyState.selectedSensorId
    state.segments = historyState.segments.map(segment => ({
      a: segment.a ? { ...segment.a } : null,
      b: segment.b ? { ...segment.b } : null
    }))
    state.startPoint = historyState.startPoint ? { ...historyState.startPoint } : null
    addSensorTool.tab.innerHTML = ''
    state.nextSensorId = historyState.nextSensorId
    state.sensors = historyState.sensors.map(sensor => Sensor.fromHistory(sensor))
    state.labelsOccupied = []
    state.sensorNameChangedOnce = historyState.sensorNameChangedOnce
    state.flowStartSensorId = historyState.flowStartSensorId ?? null
    state.flowPreviewWorld = historyState.flowPreviewWorld ? { ...historyState.flowPreviewWorld } : null
    state.groupSensorIds = [...historyState.groupSensorIds]
    state.groups = historyState.groups.map(group => ({
      ...group,
      sensorIds: group.sensorIds ? [...group.sensorIds] : []
    }))
    state.visibleSensors = historyState.visibleSensors ?? true
    state.visiblePointDistances = historyState.visiblePointDistances ?? true
    state.visibleLinearFlows = historyState.visibleLinearFlows ?? true
    state.visibleLables = historyState.visibleLables ?? true
    pathcalculatorTool.updateFields()
    state.historyIndex = index
    state.normalizeGroupState()
  }
}

export let historyTool = null

export function initHistoryTool () {
  historyTool = new HistoryTool()
  return historyTool
}
